/**
 * DeepRestCard — "Tonight's Deep Rest" on the daily dashboard, and the
 * first-session player at the end of onboarding (source="onboarding").
 *
 * States: nothing yet / preparing (polls while queued or generating) / ready
 * (player) / failed (retry). Fresh sessions are Pro+ and capped; the 403 comes
 * back as an UpgradePrompt. Replays are free for everyone, including the
 * onboarding session a free user keeps.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { Moon, Loader2, RefreshCw, Sparkles, ChevronDown, ChevronUp } from 'lucide-react';
import { listRestSessions, createRestSession, getRestSession } from '../lib/api';
import DeepRestPlayer from './DeepRestPlayer';
import UpgradePrompt from './UpgradePrompt';
import Nudge from './Nudge';
import { useNudge } from '../lib/hints';

const POLL_MS = 5000;
const POLL_MAX_MS = 4 * 60 * 1000;

function minutes(sec) {
  return sec ? `${Math.max(1, Math.round(sec / 60))} min` : '';
}

const KIND_META = {
  prime: { title: 'Prime for the day', blurb: 'Five to seven minutes that rehearse the plan you just made, then hand you the day.', make: 'Make this morning\'s' },
  rest: { title: "Tonight's Rest", blurb: 'Built from today: what got done, what didn\'t, and the seed you set for tomorrow, rehearsed until it feels decided.', make: 'Make tonight\'s' },
  reset: { title: 'Reset', blurb: 'A whole-life rest, eight to ten minutes, that rehearses your current coaching commitment in your own words. Areas have their own, in the Coach tab.', make: 'Make one now' },
};
const LENGTHS = [['short', '5'], ['standard', '10'], ['long', '15']];

export default function DeepRestCard({ source = null, kind = null, canCreate = false, autoPlayWhenReady = false, heading, className = '', threadId = null, threadName = null, onSessionsLoaded }) {
  const [sessions, setSessions] = useState(null);
  const [enabled, setEnabled] = useState(true);
  const [creating, setCreating] = useState(false);
  const [limitError, setLimitError] = useState(null);
  const [showLibrary, setShowLibrary] = useState(false);
  const [selected, setSelected] = useState(null);
  const [length, setLength] = useState('standard');
  const pollStart = useRef(null);

  const load = useCallback(async () => {
    try {
      const res = await listRestSessions({ limit: 6, source: source || undefined, threadId: threadId || undefined, kind: (!threadId && kind) || undefined });
      if (res.error) return;
      setSessions(res.sessions || []);
      setEnabled(res.enabled !== false);
      onSessionsLoaded?.(res.sessions || []);
    } catch {
      setSessions((s) => s || []);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [source, threadId, kind]);

  useEffect(() => { load(); }, [load]);

  const latest = sessions?.[0] || null;
  const current = selected || latest;
  const pending = latest && (latest.status === 'queued' || latest.status === 'generating');

  // Poll a pending session until it is ready or we give up.
  useEffect(() => {
    if (!pending) { pollStart.current = null; return undefined; }
    if (!pollStart.current) pollStart.current = Date.now();
    const t = setInterval(async () => {
      if (Date.now() - pollStart.current > POLL_MAX_MS) { clearInterval(t); return; }
      try {
        const res = await getRestSession(latest.id);
        const s = res.session;
        if (s && s.status !== 'queued' && s.status !== 'generating') {
          clearInterval(t);
          load();
        }
      } catch { /* keep polling */ }
    }, POLL_MS);
    return () => clearInterval(t);
  }, [pending, latest?.id, load]);

  const create = async () => {
    setCreating(true);
    setLimitError(null);
    try {
      const res = await createRestSession({ ...(threadId ? { thread_id: threadId } : {}), ...(kind && !threadId ? { kind } : {}), length });
      if (res.error) { setLimitError(res); return; }
      await load();
    } catch {
      /* card stays as is */
    } finally {
      setCreating(false);
    }
  };

  const readyCount = (sessions || []).filter(x => x.status === 'ready').length;
  const preparingNudge = useNudge('rest_preparing', !!pending && !threadId);
  const readyNudge = useNudge('rest_ready', !pending && current?.status === 'ready' && !threadId);
  const pushNudge = useNudge('push_enable', readyCount >= 2 && !threadId && !source);

  if (sessions === null) return null;         // loading: no flash
  if (!enabled) return null;                  // feature not configured: invisible everywhere

  const meta = KIND_META[kind] || KIND_META.reset;
  const title = heading || (threadName ? `Deep Rest for ${threadName}` : source === 'onboarding' ? 'Your first Deep Rest' : meta.title);

  return (
    <div className={`bg-slate-800/60 border border-indigo-900/40 rounded-lg p-4 ${className}`}>
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2">
          <Moon size={16} className="text-indigo-400" />
          <h3 className="text-sm font-medium text-slate-200">{title}</h3>
        </div>
        {current?.status === 'ready' && current.duration_seconds && (
          <span className="text-[11px] text-slate-500">{minutes(current.duration_seconds)}</span>
        )}
      </div>

      {pending && (
        <div className="flex items-start gap-2 text-xs text-slate-400">
          <Loader2 size={14} className="animate-spin text-indigo-400 mt-0.5 flex-shrink-0" />
          <p>
            Preparing a guided rest built from what you told the Navigator. About a minute.
            {source === 'onboarding' ? ' Put headphones on.' : ''}
          </p>
        </div>
      )}

      {!pending && current?.status === 'ready' && (
        <DeepRestPlayer session={current} autoPlay={autoPlayWhenReady} compact={source === 'onboarding'} />
      )}

      {!pending && current?.status === 'failed' && (
        <p className="text-xs text-amber-300">{current.error || 'Could not generate this session.'}</p>
      )}

      {!pending && !current && (
        <p className="text-xs text-slate-400">
          {threadName
            ? `A guided rest built from this area alone: its commitment, its trip wire, its next step. Nothing here yet.`
            : `${meta.blurb} Nothing here yet.`}
        </p>
      )}

      {!pending && !source && (
        <div className="flex items-center justify-between mt-3 gap-2">
          {canCreate ? (
            <div className="flex items-center gap-2 min-w-0">
              <button
                onClick={create}
                disabled={creating}
                className="text-xs text-indigo-300 hover:text-indigo-200 flex items-center gap-1.5 disabled:opacity-60 flex-shrink-0"
              >
                {creating ? <Loader2 size={12} className="animate-spin" /> : (current ? <RefreshCw size={12} /> : <Sparkles size={12} />)}
                {current ? 'Make a fresh one' : (threadName ? 'Make one for this area' : meta.make)}
              </button>
              <span className="inline-flex rounded-md border border-slate-700 overflow-hidden" role="group" aria-label="Length in minutes">
                {LENGTHS.map(([k, label]) => (
                  <button key={k} onClick={() => setLength(k)} aria-pressed={length === k}
                    className={`px-2 py-0.5 text-[11px] ${length === k ? 'bg-indigo-900/50 text-indigo-200' : 'text-slate-500 hover:text-slate-300'}`}>
                    {label}
                  </button>
                ))}
                <span className="px-1.5 py-0.5 text-[10px] text-slate-600">min</span>
              </span>
            </div>
          ) : (
            <span className="text-[11px] text-slate-500">Fresh sessions come with the trial. Replays are always yours.</span>
          )}
          {sessions.length > 1 && (
            <button onClick={() => setShowLibrary(o => !o)} className="text-[11px] text-slate-500 hover:text-slate-300 flex items-center gap-1">
              Earlier {showLibrary ? <ChevronUp size={12} /> : <ChevronDown size={12} />}
            </button>
          )}
        </div>
      )}

      {showLibrary && sessions.length > 1 && (
        <ul className="mt-2 space-y-1">
          {sessions.slice(1).filter(s => s.status === 'ready').map((s) => (
            <li key={s.id}>
              <button
                onClick={() => setSelected(s)}
                className={`w-full text-left text-xs px-2 py-1.5 rounded hover:bg-slate-700/40 ${selected?.id === s.id ? 'text-indigo-300' : 'text-slate-400'}`}
              >
                {s.title || 'Deep Rest'} <span className="text-slate-600">· {minutes(s.duration_seconds)} · {String(s.created_at || '').slice(0, 10)}</span>
              </button>
            </li>
          ))}
        </ul>
      )}

      {limitError && (
        <div className="mt-3">
          <UpgradePrompt limitError={limitError} onDismiss={() => setLimitError(null)} />
        </div>
      )}
      {preparingNudge.show && (
        <Nudge title="It's writing your session." text="About a minute: a script from your own words, a voice, and music. You can keep using the app; it will appear here." onDismiss={preparingNudge.dismiss} />
      )}
      {!preparingNudge.show && readyNudge.show && (
        <Nudge title="Your session is ready." text="Headphones make it better. Every word is readable under Read the script. Not for use while driving." onDismiss={readyNudge.dismiss} />
      )}
      {!preparingNudge.show && !readyNudge.show && pushNudge.show && (
        <Nudge title="Want to be told when the next one is ready?" text="Turn on notifications under You, then Settings. On iPhone, add the Navigator to your Home Screen first." onDismiss={pushNudge.dismiss} />
      )}
    </div>
  );
}
